import { useEffect, useRef, useState } from "react";
import { Bot, Send, Loader2, X, Trash2, Undo2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";

const visible = (t) => t.split("```actions")[0].trim();
const EXAMPLES = ["Cut the first clip at 3 seconds and add a cross dissolve", "Give it a warm golden-hour grade with deep shadows", "Duck the music under dialogue and fade the last clip out", "Add a golden light leak at the playhead"];

const Msg = ({ m, canUndo, onUndo }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`agent-msg-${m.role}`}>
    <div className={`max-w-[88%] rounded-lg px-3 py-2 text-[12px] leading-relaxed ${m.role === "user" ? "bg-[#FFB800]/15 border border-[#FFB800]/30 text-slate-100" : "bg-[#13151C] border border-[#2A2E3D] text-slate-200"}`}>
      <p className="whitespace-pre-wrap">{m.content || (m.pending ? "…" : "")}</p>
      {m.log?.length > 0 && (
        <ul className="mt-2 space-y-0.5 border-t border-[#2A2E3D] pt-1.5" data-testid="agent-action-log">
          {m.log.map((l, i) => (
            <li key={i} className={`flex items-start gap-1.5 text-[10px] ${l.ok ? "text-emerald-300" : "text-amber-300"}`}>
              {l.ok ? <CheckCircle2 className="w-3 h-3 mt-px shrink-0" /> : <AlertCircle className="w-3 h-3 mt-px shrink-0" />}{l.text}
            </li>
          ))}
        </ul>
      )}
      {canUndo && <button onClick={onUndo} data-testid="agent-undo-button" className="mt-1.5 flex items-center gap-1 text-[10px] text-slate-400 hover:text-white"><Undo2 className="w-3 h-3" />Undo these edits</button>}
    </div>
  </div>
);

export const AgentPanel = ({ projectId, getState, onRun, onUndo, onClose }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [undoId, setUndoId] = useState(null);
  const end = useRef(null);

  useEffect(() => { api.get(`/agent/${projectId}/messages`).then((r) => setMsgs((prev) => [...r.data.filter((d) => !prev.some((p) => p.id === d.id)), ...prev])).catch(() => {}); }, [projectId]);
  useEffect(() => end.current?.scrollIntoView({ block: "end" }), [msgs]);

  const send = async (msg) => {
    const message = (msg ?? text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    const tmp = { id: `p-${Date.now()}`, role: "assistant", content: "", pending: true };
    setMsgs((l) => [...l, { id: `u-${Date.now()}`, role: "user", content: message }, tmp]);
    const upd = (p) => setMsgs((l) => l.map((m) => (m.id === tmp.id ? { ...m, ...p } : m)));
    try {
      const res = await fetch(`${API}/agent/${projectId}/chat`, { method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message, state: getState() }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Request failed (${res.status})`);
      const reader = res.body.getReader(), dec = new TextDecoder();
      let buf = "", acc = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.type === "delta") { acc += ev.text; upd({ content: visible(acc) }); }
          if (ev.type === "error") throw new Error(ev.error);
          if (ev.type === "done") {
            const log = ev.actions.length ? onRun(ev.actions, ev.id) : [];
            upd({ id: ev.id, content: ev.reply, actions: ev.actions, log, pending: false });
            if (ev.actions.length) { setUndoId(ev.id); api.patch(`/agent/${projectId}/messages/${ev.id}`, { log }).catch(() => {}); }
          }
        }
      }
    } catch (e) {
      upd({ content: e.message || "Something went wrong", pending: false, log: [{ ok: false, text: "No changes made" }] });
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Clear this project's AI Editor conversation?")) return;
    await api.delete(`/agent/${projectId}/messages`); setMsgs([]); setUndoId(null);
  };

  return (
    <aside className="fixed right-0 top-14 bottom-0 w-[380px] z-40 bg-[#0B0C10]/95 backdrop-blur-xl border-l border-[#FFB800]/30 shadow-[-12px_0_40px_rgba(0,0,0,0.6)] flex flex-col animate-in slide-in-from-right duration-200" data-testid="agent-panel">
      <div className="h-11 px-3 flex items-center justify-between border-b border-[#2A2E3D]">
        <span className="flex items-center gap-2 text-[12px] font-semibold"><Bot className="w-4 h-4 text-[#FFB800]" />AI Editor<span className="font-mono-ui text-[9px] text-slate-500">CLAUDE OPUS</span></span>
        <div className="flex gap-1">
          <button onClick={clear} data-testid="agent-clear-button" title="Clear the conversation" className="p-1.5 text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
          <button onClick={onClose} data-testid="agent-close-button" title="Close the AI Editor" className="p-1.5 text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="agent-messages">
        {!msgs.length && (
          <div className="text-[12px] text-slate-400 space-y-2" data-testid="agent-empty">
            <p>Your film editor, colourist and mixer. Ask for any edit and it's applied straight away — every change can be undone.</p>
            {EXAMPLES.map((e) => (
              <button key={e} onClick={() => send(e)} className="block w-full text-left text-[11px] px-2.5 py-1.5 rounded border border-[#2A2E3D] hover:border-[#FFB800]/60 hover:text-white transition-colors" data-testid="agent-example">{e}</button>
            ))}
          </div>
        )}
        {msgs.map((m) => <Msg key={m.id} m={m} canUndo={!busy && m.id === undoId} onUndo={() => { onUndo(m.id); setUndoId(null); }} />)}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-[#2A2E3D] flex gap-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={4000} placeholder="e.g. tighten the opening and add a dip to black…"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="flex-1 resize-none bg-[#13151C] border border-[#2A2E3D] rounded-md px-2.5 py-2 text-[12px] text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#FFB800]/60" data-testid="agent-input" />
        <button type="submit" disabled={busy || !text.trim()} data-testid="agent-send-button" title="Send to the AI Editor"
          className="self-end h-9 w-9 flex items-center justify-center rounded-md bg-[#FFB800] text-[#0B0C10] disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </aside>
  );
};
